import ArticleLayout from '@/components/feature/ArticleLayout';
import { useLang } from '@/hooks/useLang';

export default function AllChinaBuyShippingPage() {
  const currentLang = useLang();

  return (
    <ArticleLayout
      title="AllChinaBuy Shipping Cost & Complete Shipping Guide 2026"
      metaDesc="Complete guide to AllChinaBuy shipping costs, methods, delivery times, and factors that affect international shipping. Learn how to estimate and reduce shipping expenses."
      h1="AllChinaBuy Shipping Cost & Complete Guide 2026"
      author="Editorial Team"
      publishedDate="2026-03-01"
      updatedDate="2026-08-26"
      breadcrumbs={[
        { label: 'Home', href: `/${currentLang}/` },
        { label: 'Shipping', href: `/${currentLang}/allchinabuy-shipping/` },
      ]}
    >
      <div className="prose max-w-none">
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-10">
          <p className="text-lg text-foreground-800 leading-relaxed m-0">
            <strong>AllChinaBuy shipping costs</strong> vary based on destination, parcel weight, dimensions, shipping method, and carrier. Understanding these factors helps you plan purchases and estimate total costs before buying. This guide explains shipping methods, cost factors, delivery times, and practical tips for reducing shipping expenses.
          </p>
        </div>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Understanding Shipping Costs</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Shipping from China to international destinations involves multiple cost components. The primary factors include the base shipping rate (determined by weight and dimensions), fuel surcharges, remote area delivery fees, and potential customs duties or taxes in the destination country.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Weight is typically the most significant factor. Most carriers use either actual weight or volumetric weight (based on parcel dimensions), whichever is greater. This means lightweight but bulky items may cost more to ship than heavier but compact items.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Shipping Methods Comparison</h2>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead className="bg-background-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Method</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Speed</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Cost</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Tracking</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Best For</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {[
                ['Express Courier', '3-10 days', 'Higher', 'Full tracking', 'Urgent, valuable items'],
                ['Standard Air', '7-20 days', 'Moderate', 'Basic tracking', 'Regular orders'],
                ['Economy Air', '15-30 days', 'Lower', 'Limited tracking', 'Non-urgent, bulk orders'],
                ['Sea Freight', '30-60 days', 'Lowest', 'Limited tracking', 'Very large, heavy items'],
              ].map(([method, speed, cost, tracking, bestFor]) => (
                <tr key={method} className="bg-background-50">
                  <td className="px-4 py-3 font-medium text-foreground-800">{method}</td>
                  <td className="px-4 py-3 text-foreground-600">{speed}</td>
                  <td className="px-4 py-3 text-foreground-600">{cost}</td>
                  <td className="px-4 py-3 text-foreground-600">{tracking}</td>
                  <td className="px-4 py-3 text-foreground-600">{bestFor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Factors That Affect Shipping Costs</h2>
        <ul className="list-disc list-inside space-y-2 text-foreground-700 mb-6 pl-1">
          <li><strong>Destination country</strong> — Different regions have different base rates and customs procedures.</li>
          <li><strong>Parcel weight</strong> — Heavier parcels cost more across all shipping methods.</li>
          <li><strong>Volumetric weight</strong> — Bulky items may be charged based on dimensions rather than actual weight.</li>
          <li><strong>Shipping method</strong> — Express services cost significantly more than economy options.</li>
          <li><strong>Insurance</strong> — Optional shipping insurance adds cost but provides protection for lost or damaged items.</li>
          <li><strong>Remote area surcharge</strong> — Deliveries to remote or rural areas may incur additional fees.</li>
          <li><strong>Customs duties & taxes</strong> — Import fees vary by country and product category.</li>
        </ul>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Tips to Reduce Shipping Costs</h2>
        <ol className="list-decimal list-inside space-y-3 text-foreground-700 mb-6 pl-1">
          <li><strong>Consolidate parcels</strong> — Combine multiple items into one shipment to reduce per-parcel fees.</li>
          <li><strong>Remove unnecessary packaging</strong> — Ask for shoeboxes and excess packaging to be removed to reduce weight.</li>
          <li><strong>Choose economy shipping</strong> — If delivery time is not critical, economy options save significantly.</li>
          <li><strong>Plan purchases together</strong> — Order multiple items at once to consolidate.</li>
          <li><strong>Compare shipping lines</strong> — Different carriers offer different rates for the same destination.</li>
          <li><strong>Consider sea freight for bulk</strong> — Large orders may benefit from slower but cheaper sea shipping.</li>
        </ol>

        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Customs & Import Duties</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Import duties and taxes are determined by your destination country\'s customs regulations. These fees are typically separate from shipping costs and are the buyer\'s responsibility. Factors affecting customs charges include product category, declared value, destination country thresholds, and local regulations.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          Some countries offer duty-free thresholds for low-value imports. Exceeding these thresholds may result in additional VAT, customs duties, and processing fees. Research your country\'s import rules before ordering.
        </p>

        <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 mb-10">
          <h3 className="font-semibold text-foreground-900 mb-2">Important Note</h3>
          <p className="text-sm text-foreground-600 leading-relaxed">
            Shipping costs and delivery times are estimates based on general industry practices. Actual costs and times vary by carrier, destination, and current conditions. Always verify current shipping rates with the platform before confirming orders. We do not provide live shipping quotes or guarantee delivery times.
          </p>
        </div>

        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
            Explore Products on AllChinaBuy
          </h3>
          <p className="text-sm text-foreground-600 mb-5">
            Browse the spreadsheet to find products and plan your shipping strategy.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors"
          >
            Visit Main Website →
          </a>
        </div>

        <div className="mt-10 pt-6 border-t border-background-200/70">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <strong>Disclaimer:</strong> This guide provides general information for educational purposes. Shipping rates, methods, and delivery times change frequently. We do not guarantee accuracy of shipping cost estimates or delivery timelines. Always verify current information with the shipping platform before purchasing.
          </p>
        </div>
      </div>
    </ArticleLayout>
  );
}